import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import Link from 'components/Globals/Base/Link';
import type { EventData } from 'services/Gtm/entities';
import { useEffect, useMemo } from 'react';
import type { Hub } from 'interfaces/Home';
import Pagination from 'components/Pagination/Pagination';
import { Microsites, SectionTypes } from 'constants/constants';
import { useRouter } from 'next/router';
import { useScrollToElement } from 'hooks/useScrollToElement';
import { useHeaderHeight } from 'hooks/useHeaderHeight';
import ArticleCard from 'components/Globals/ArticleCard';
import { ARTICLE_SECTION_TEST_IDS } from 'constants/testsIds/articleComponents';
import type { PagePagination } from 'utils/miscUtils/calculatePageInfo';
import { insertAfterFirstWord } from 'utils/stringUtils';
import { SectionVariant } from 'interfaces/Section';
import * as S from './ArticleSectionStyles';

type Variant = 'latest-articles' | 'more-articles';

export interface LatestArticlesPagination {
  slug: string;
  taxonomy: string;
  numPostsPerPage: number;
  totalArticles: number;
  pageNum: number;
  pageInfo: string;
  limitPagesCount?: boolean;
}

export interface LatestArticlesProps {
  pagination: PagePagination;
  platform: string;
  title: string;
  pinnedLinks?: Hub['children'];
  variant: Variant;
  trackingData: EventData;
  showPageInfo?: boolean;
  articles?: ArticlePreview[];
  sectionType?: SectionTypes;
  sectionTitle?: string;
  className?: string;
}

const LatestArticles = (props: LatestArticlesProps) => {
  const {
    showPageInfo = false,
    title,
    pagination,
    platform,
    pinnedLinks,
    trackingData,
    variant,
    articles,
    sectionType,
    sectionTitle,
    className,
  } = props;
  const [executeScroll, elRef] = useScrollToElement<HTMLHeadingElement>();
  const { headerHeight } = useHeaderHeight();
  const router = useRouter();
  const { isReady } = router;

  const isFirstPage = pagination.currentPage === 1;

  useEffect(() => {
    if (!isFirstPage && isReady) {
      executeScroll();
    }
  }, [isFirstPage, isReady, executeScroll]);

  const getLayoutVariant = useMemo(() => {
    if (platform === Microsites.Recommends) {
      const isRecommendsCategoryPage = sectionType !== undefined && Object.values(SectionTypes).includes(sectionType);
      return isRecommendsCategoryPage ? 'paginated-content-category-page' : 'paginated-content';
    }
    if (platform === Microsites.Education) return 'paginated-content';
    return 'paginated-content-3-columns';
  }, [platform, sectionType]);

  if (!articles || articles?.length === 0) return null;

  const shouldShowPageInfo =
    showPageInfo && pagination && (platform === Microsites.Recommends || platform === Microsites.Education);

  const dataCy =
    variant === 'more-articles' ? ARTICLE_SECTION_TEST_IDS.MORE_ARTICLES : ARTICLE_SECTION_TEST_IDS.LATEST_ARTICLES;

  return (
    <S.Wrapper
      $variant={getLayoutVariant}
      className={`${platform} ${className}`}
      data-cy={dataCy}
    >
      <>
        <S.Title
          as='div'
          $fontFamily='saol'
          $size={{ default: 'level3', xlMin: 'level2' }}
          $centered={variant === 'more-articles'}
          $headerHeight={headerHeight + 100}
          className='section-title'
          data-cy={`${dataCy}-${ARTICLE_SECTION_TEST_IDS.TITLE}`}
          ref={elRef}
        >
          {title && <h2>{sectionTitle ? insertAfterFirstWord(title, sectionTitle) : title}</h2>}
        </S.Title>

        {pinnedLinks && (
          <S.PinnedLinks
            className='pinned-links'
            data-cy={`${dataCy}-${ARTICLE_SECTION_TEST_IDS.PINNED_LINKS}`}
          >
            {pinnedLinks.map((link) => (
              <Link
                key={link.link}
                href={link.link}
                type='brandAccentSecondary'
                ariaLabel={`Go to ${link.name}`}
                trackingData={{
                  ...trackingData,
                  eventAction: 'title click',
                  eventCategory: 'navigation',
                  eventLabel: `category click : ${link.name}`,
                }}
              >
                {link.name}
              </Link>
            ))}
          </S.PinnedLinks>
        )}

        {shouldShowPageInfo && (
          <S.PageInfo
            as='h4'
            $size={{ default: 'level6' }}
            $fontFamily='graphikCond'
            className='page-info'
            data-cy={ARTICLE_SECTION_TEST_IDS.PAGE_INFO}
          >
            {pagination.pageInfo}
          </S.PageInfo>
        )}
        {articles?.length > 0 && (
          <div
            className='articles-list'
            data-cy={`${dataCy}-${ARTICLE_SECTION_TEST_IDS.ARTICLES_LIST}`}
          >
            {articles.map((item) => (
              <ArticleCard
                key={item.titleLink}
                article={item}
                layoutVariant={sectionType === SectionTypes.MORE_SECTION ? 'hero' : 'card-lg'}
                dataVariant={
                  sectionType === SectionTypes.MORE_SECTION ? 'hub' : `list-${SectionVariant.LatestArticles}`
                }
                eyebrowType='red'
                titleSize={{ default: 'level6', lgMin: 'level4' }}
                trackingData={{
                  ...trackingData,
                  eventAction: 'title click',
                  eventCategory: 'navigation',
                  eventLabel: item.title,
                }}
              />
            ))}
          </div>
        )}
      </>
      <Pagination pagination={pagination} />
    </S.Wrapper>
  );
};

export default LatestArticles;
